import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Badge,
  Button,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  MdOutlineDelete,
  MdPhone,
  MdPlace,
  MdAdd,
  MdDateRange,
  MdFilterList,
  MdClear,
} from "react-icons/md";
import { LiaEditSolid } from "react-icons/lia";
import { HiOutlineDotsVertical } from "react-icons/hi";
import {
  FaUserCircle,
  FaMoneyBillWave,
  FaHistory,
  FaFileExcel,
  FaFilePdf,
} from "react-icons/fa";
import TableUI from "../../components/TableUI";
import { ActionButton } from "../../components/Buttons";
import { fetchStaff } from "../../slice/StaffSlice";
import {
  fetchStaffAdvance,
  addStaffAdvance,
  updateStaffAdvance,
  deleteStaffAdvance,
} from "../../slice/StaffAdvanceSlice";
import CustomModal from "../../components/Modal";
import NotifyData from "../../components/NotifyData";
import { PDFDownloadLink } from "@react-pdf/renderer";
import StaffAdvancePDF from "./StaffAdvancePDF";

const StaffAdvance = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { Staff } = useSelector((state) => state.Staff);
  const { advances } = useSelector((state) => state.StaffAdvance);

  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({
    amount: "",
    type: "credit",
    entry_date: new Date().toISOString().split("T")[0],
  });

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => {
    dispatch(fetchStaff());
    dispatch(fetchStaffAdvance({ id, fromDate, toDate }));
  }, [dispatch, id]); // Depend only on initial load or manual filter triggers?
  // Should probably remove fromDate/toDate from dependency if we want explicit filter button.
  // Actually, standard pattern is to fetch on mount.

  const handleFilter = () => {
    dispatch(fetchStaffAdvance({ id, fromDate, toDate }));
  };

  const handleClear = () => {
    setFromDate("");
    setToDate("");
    dispatch(fetchStaffAdvance({ id, fromDate: "", toDate: "" }));
  };

  const staffMember = Staff?.find((s) => String(s.id) === String(id));

  const handleSubmit = async () => {
    try {
      if (editMode) {
        await dispatch(
          updateStaffAdvance({ id: selectedId, ...formData }),
        ).unwrap();
        NotifyData("Updated Successfully", "success");
      } else {
        await dispatch(
          addStaffAdvance({
            ...formData,
            staff_id: id,
            staff_name: staffMember?.Name,
          }),
        ).unwrap();
        NotifyData("Added Successfully", "success");
      }
      setShowModal(false);
      dispatch(fetchStaffAdvance({ id, fromDate, toDate }));
      dispatch(fetchStaff());
    } catch (err) {
      NotifyData("Error processing request", "error");
    }
  };

  const handleDelete = async (db_id) => {
    if (window.confirm("Delete this record and reverse balance?")) {
      await dispatch(deleteStaffAdvance(db_id)).unwrap();
      dispatch(fetchStaff());
      NotifyData("Deleted Successfully", "success");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  // Calculate Totals
  const totalCredit = advances?.reduce(
    (acc, curr) => (curr.type === "credit" ? acc + Number(curr.amount) : acc),
    0,
  );
  const totalDebit = advances?.reduce(
    (acc, curr) => (curr.type === "debit" ? acc + Number(curr.amount) : acc),
    0,
  );
  const netBalance = totalDebit - totalCredit;

  const exportToCSV = () => {
    const csvRows = [];

    // Header Info
    csvRows.push([`Staff Name: ${staffMember?.Name || ""}`]);
    csvRows.push([`Generated Date: ${new Date().toLocaleDateString("en-GB")}`]);
    if (fromDate || toDate) {
      csvRows.push([
        `Period: ${formatDate(fromDate)} to ${formatDate(toDate)}`,
      ]);
    }
    csvRows.push([]); // Blank Row

    // Table Headers
    const headers = ["Date", "Type", "Credit", "Debit"];
    csvRows.push(headers.join(","));

    // Table Body
    advances.forEach((adv) => {
      const date = formatDate(adv.entry_date);
      const type = adv.type;
      const credit = adv.type === "credit" ? adv.amount : "";
      const debit = adv.type === "debit" ? adv.amount : "";
      // Prepend \t to force text format in Excel for Date
      csvRows.push([`\t${date}`, type, credit, debit].join(","));
    });

    // Totals Row
    csvRows.push(
      ["Total", "", totalCredit.toFixed(2), totalDebit.toFixed(2)].join(","),
    );

    // Net Balance Row
    csvRows.push([]);
    csvRows.push(["", "", "Net Balance Due:", netBalance.toFixed(2)].join(","));

    const blob = new Blob(["\uFEFF" + csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Staff_Advance_${staffMember?.Name || "Report"}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const advanceHead = ["Date", "Credit", "Debit"];
  const advanceData =
    advances?.map((adv) => ({
      values: [
        formatDate(adv.entry_date),
        <span className="text-danger fw-bold">
          {adv.type === "credit"
            ? `₹${parseFloat(adv.amount).toFixed(2)}`
            : "-"}
        </span>,
        <span className="text-success fw-bold">
          {adv.type === "debit" ? `₹${parseFloat(adv.amount).toFixed(2)}` : "-"}
        </span>,
        <ActionButton
          options={[
            {
              label: "Edit",
              icon: <LiaEditSolid />,
              onClick: () => {
                setEditMode(true);
                setSelectedId(adv.id);
                setFormData({
                  amount: adv.amount,
                  type: adv.type,
                  entry_date: adv.entry_date,
                });
                setShowModal(true);
              },
            },
            {
              label: "Delete",
              icon: <MdOutlineDelete />,
              onClick: () => handleDelete(adv.id),
            },
          ]}
          label={<HiOutlineDotsVertical />}
        />,
      ],
    })) || [];

  if (advanceData.length > 0) {
    advanceData.push({
      values: [
        <span className="fw-bold ">Total</span>,
        <span className="fw-bold text-danger">₹{totalCredit?.toFixed(2)}</span>,
        <span className="fw-bold text-success">₹{totalDebit?.toFixed(2)}</span>,
        "",
      ],
    });
  }

  return (
    <div id="main">
      <Container fluid className="py-4 bg-light min-vh-100">
        <Container>
          {/* Header Section */}
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
            <div>
              <h2 className="fw-bold text-dark mb-0">
                Staff Advance Management
              </h2>
              <p className="text-muted mb-0">
                Manage advances and repayments for {staffMember?.Name}
              </p>
            </div>
          </div>

          <Row className="mb-4 g-4">
            {/* Staff Profile Card */}
            <Col md={6} lg={7}>
              <Card className="shadow-sm border-0 h-100 rounded-4 overflow-hidden">
                <div
                  className="bg-gradient-primary p-2"
                  style={{
                    background:
                      "linear-gradient(90deg, #1466a9ff 0%, #4e53abff 100%)",
                    height: "10px",
                  }}
                ></div>
                <Card.Body className="d-flex align-items-center p-4">
                  <div className="me-4 text-primary">
                    <FaUserCircle size={64} />
                  </div>
                  <div>
                    <h4 className="fw-bold mb-1">
                      {staffMember?.Name || "Loading..."}
                    </h4>
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      {Array.isArray(staffMember?.staff_type) ? (
                        staffMember.staff_type.map((type, index) => (
                          <Badge key={index} bg="danger">
                            {type}
                          </Badge>
                        ))
                      ) : (
                        <Badge bg="danger">
                          {staffMember?.staff_type || staffMember?.Staff_Type}
                        </Badge>
                      )}
                    </div>
                    <div className="d-flex flex-wrap gap-3 text-muted">
                      <small className="d-flex align-items-center gap-1">
                        <MdPhone /> {staffMember?.Mobile_Number || "N/A"}
                      </small>
                      <small className="d-flex align-items-center gap-1">
                        <MdPlace /> {staffMember?.Place || "N/A"}
                      </small>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Balance Stat Card */}
            <Col md={6} lg={5}>
              <Card
                className="shadow-sm border-0 h-100 rounded-4 overflow-hidden text-white"
                style={{
                  background:
                    "linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)",
                }}
              >
                <Card.Body className="position-relative p-4 d-flex flex-column justify-content-center">
                  <FaMoneyBillWave
                    className="position-absolute opacity-25"
                    size={120}
                    style={{ right: -20, bottom: -20 }}
                  />
                  <h6 className="text-uppercase opacity-75 mb-2 letter-spacing-1">
                    Current Balance Due
                  </h6>
                  <h1 className="display-4 fw-bold mb-0">
                    ₹ {staffMember?.staff_advance || 0}
                  </h1>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Transactions Table */}
          <Card className="shadow-sm border-0 rounded-4 overflow-hidden">
            <Card.Header className="bg-white py-3 px-4 border-bottom">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 w-100">
                <div className="d-flex align-items-center gap-2">
                  <FaHistory className="text-secondary" />
                  <h5 className="mb-0 fw-bold text-dark">
                    Transaction History
                  </h5>
                </div>

                <div className="d-flex flex-wrap gap-2 align-items-end">
                  <div>
                    <Form.Control
                      type="date"
                      size="sm"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      title="From Date"
                    />
                  </div>
                  <div>
                    <Form.Control
                      type="date"
                      size="sm"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      title="To Date"
                    />
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleFilter}
                    className="shadow-sm d-flex align-items-center gap-1 border-0 "
                    style={{
                      background:
                        "linear-gradient(90deg, #FF9966 0%, #FF5E62 100%)",
                      color: "white",
                      fontWeight: "bold",
                    }}
                  >
                    <MdFilterList /> Apply
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleClear}
                    className="shadow-sm d-flex align-items-center gap-1 border-0"
                    style={{
                      background:
                        "linear-gradient(90deg, #FF9966 0%, #FF5E62 100%)",
                      color: "white",
                      fontWeight: "bold",
                    }}
                  >
                    <MdClear /> Clear
                  </Button>

                  <div className="vr mx-2"></div>

                  <Button
                    variant="success"
                    size="sm"
                    onClick={exportToCSV}
                    className="d-flex align-items-center gap-2"
                  >
                    <FaFileExcel />
                  </Button>
                  <PDFDownloadLink
                    document={
                      <StaffAdvancePDF
                        data={advances}
                        staffName={staffMember?.Name}
                        fromDate={fromDate}
                        toDate={toDate}
                      />
                    }
                    fileName={`Staff_Advance_${staffMember?.Name || "Report"}.pdf`}
                    style={{ textDecoration: "none" }}
                  >
                    {({ loading }) => (
                      <Button
                        variant="danger"
                        size="sm"
                        className="d-flex align-items-center gap-2"
                        disabled={loading}
                      >
                        <FaFilePdf />
                      </Button>
                    )}
                  </PDFDownloadLink>
                  <Button
                    variant="light"
                    size="sm"
                    className="d-flex align-items-center gap-2 shadow-sm  px-3 text-white border-0"
                    style={{
                      background:
                        "linear-gradient(90deg, #22a54eff 0%, #0f691aff 100%)",
                    }}
                    onClick={() => {
                      setEditMode(false);
                      setFormData({
                        amount: "",
                        type: "debit", // Default to Debit (Advance)
                        entry_date: new Date().toISOString().split("T")[0],
                      });
                      setShowModal(true);
                    }}
                  >
                    <MdAdd size={20} /> New Entry
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <TableUI
                headers={advanceHead}
                body={advanceData}
                className="mb-0 align-middle"
              />
            </Card.Body>
          </Card>

          {/* Modal */}
          <CustomModal
            show={showModal}
            setShow={setShowModal}
            pageTitle={editMode ? "Edit Transaction" : "New Advance Entry"}
            OnClick={handleSubmit}
            submitButton={true}
            submitLabel="Submit"
            CancelLabel="Cancel"
            BodyComponent={
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold small">Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.entry_date}
                    onChange={(e) =>
                      setFormData({ ...formData, entry_date: e.target.value })
                    }
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold small">
                    Transaction Type
                  </Form.Label>
                  <Form.Select
                    value={formData.type}
                    onChange={(e) =>
                      setFormData({ ...formData, type: e.target.value })
                    }
                  >
                    <option value="debit">
                      Debit (Staff receives cash / Advance)
                    </option>
                    <option value="credit">
                      Credit (Staff pays back / Deduction)
                    </option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold small">Amount (₹)</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.amount}
                    onChange={(e) =>
                      setFormData({ ...formData, amount: e.target.value })
                    }
                    placeholder="0.00"
                  />
                </Form.Group>
              </Form>
            }
          />
        </Container>
      </Container>
    </div>
  );
};

export default StaffAdvance;
